import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api, authHeaders, rm, PROJECTS, PROJECT_COLOR, PLATFORMS } from './marketingShared';
import './Marketing.css';

const PLATFORM_DOT = {
  FB: '#3b5998', Instagram: '#c13584', 'Website/SEO': '#3B5BDB',
  Google: '#1a73e8', TikTok: '#111', Others: 'var(--rmc-ink3)',
};
const PROJ_SHORT = { RMC: 'RMC', 'Everyday Catering': 'Everyday', 'W Factory': 'W Factory', 'W in the Sky': 'W Sky', 'W Timber': 'W Timber', 'W Nexus': 'W Nexus' };

const fmtInt = (n) => (Number(n) || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 });
const sanitize = (s, integer) => {
  s = (s || '').replace(integer ? /[^0-9]/g : /[^0-9.,]/g, '');
  if (!integer) { const i = s.indexOf('.'); if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, ''); }
  return s;
};
const key = (kpi, project, platform = '') => `${kpi}|${project}|${platform}`;
const fmtWhen = (ts) => {
  if (!ts) return '';
  const d = new Date(ts); if (isNaN(d)) return String(ts);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getDate()} ${mon}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

function KpiInsert({ month }) {
  const { isStaff } = useAuth();
  const canEdit = isStaff;                       // any staff can key in; every change is logged
  const [rows, setRows] = useState({});          // key -> { id, value }
  const [log, setLog] = useState([]);            // edit log, newest first
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const editRef = useCallback((el) => { if (el) { el.focus(); el.select(); } }, []);
  const navRef = useRef(false);

  const refreshLog = useCallback(async () => {
    try {
      const a = await api.get(`/kpi/audit?month=${month}`, { headers: authHeaders() });
      setLog(a.data.rows);
    } catch { /* non-fatal */ }
  }, [month]);

  const load = useCallback(async () => {
    setError('');
    try {
      const [kp, au] = await Promise.all([
        api.get(`/kpi?month=${month}`, { headers: authHeaders() }),
        api.get(`/kpi/audit?month=${month}`, { headers: authHeaders() }),
      ]);
      const m = {};
      kp.data.rows.forEach((r) => { m[key(r.kpi, r.project, r.platform || '')] = { id: r.id, value: r.value }; });
      setRows(m);
      setLog(au.data.rows);
    } catch { setError('Failed to load KPIs'); }
  }, [month]);

  useEffect(() => { load(); setEditKey(null); }, [load]);

  const shown = (kpi, project, platform = '') => {
    const c = rows[key(kpi, project, platform)];
    return c ? c.value : null;
  };

  const commit = useCallback(async (kpi, project, platform, valueStr) => {
    const k = key(kpi, project, platform);
    const num = valueStr.trim() === '' ? 0 : parseFloat(sanitize(valueStr, kpi === 'leads').replace(/,/g, ''));
    const cur = rows[k];
    if (!Number.isFinite(num)) return;
    if (cur && cur.value === num) return;   // unchanged
    try {
      const res = await api.put('/kpi', { month, kpi, project, platform, value: num }, { headers: authHeaders() });
      setRows((p) => ({ ...p, [k]: { id: res.data.id, value: res.data.value } }));
      refreshLog();
    } catch (e) { setError(e.response?.data?.error || 'Could not save'); }
  }, [rows, month, refreshLog]);

  const beginEdit = (kpi, project, platform) => {
    if (!canEdit) return;
    const v = shown(kpi, project, platform);
    setEditKey(key(kpi, project, platform));
    setDraft(v == null ? '' : String(v));
  };
  const moveDown = (kpi, project, platform) => {
    if (kpi === 'leads') {
      const pi = PLATFORMS.indexOf(platform);
      if (pi < PLATFORMS.length - 1) beginEdit('leads', project, PLATFORMS[pi + 1]); else setEditKey(null);
    } else {
      const pi = PROJECTS.indexOf(project);
      if (pi < PROJECTS.length - 1) beginEdit('spend', PROJECTS[pi + 1], ''); else setEditKey(null);
    }
  };
  const onKeyDown = (e, kpi, project, platform) => {
    if (e.key === 'Enter') { e.preventDefault(); navRef.current = true; commit(kpi, project, platform, draft); moveDown(kpi, project, platform); }
    else if (e.key === 'Escape') { e.preventDefault(); navRef.current = true; setEditKey(null); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); navRef.current = true; commit(kpi, project, platform, draft); moveDown(kpi, project, platform); }
  };

  // ---- totals ----
  const leadColTotal = (project) => PLATFORMS.reduce((a, pl) => a + (shown('leads', project, pl) || 0), 0);
  const leadRowTotal = (platform) => PROJECTS.reduce((a, pr) => a + (shown('leads', pr, platform) || 0), 0);
  const leadGrand = PLATFORMS.reduce((a, pl) => a + leadRowTotal(pl), 0);
  const spendTotal = PROJECTS.reduce((a, pr) => a + (shown('spend', pr) || 0), 0);

  const leadCell = (project, platform) => {
    const k = key('leads', project, platform);
    const editing = editKey === k;
    const v = shown('leads', project, platform);
    const cls = ['mk-cell', v != null ? 'filled' : 'empty'];
    if (editing) cls.push('editing');
    return (
      <td key={platform + project} className={cls.join(' ')} onClick={() => !editing && beginEdit('leads', project, platform)}>
        {editing ? (
          <input className="mk-cell-input" inputMode="numeric" autoComplete="off" value={draft} ref={editRef}
            onChange={(e) => setDraft(sanitize(e.target.value, true))}
            onKeyPress={(e) => { if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault(); }}
            onKeyDown={(e) => onKeyDown(e, 'leads', project, platform)}
            onBlur={() => { if (navRef.current) { navRef.current = false; return; } commit('leads', project, platform, draft); setEditKey(null); }} />
        ) : (
          <span className="mk-cell-val">{v != null ? fmtInt(v) : ''}</span>
        )}
      </td>
    );
  };

  const spendRow = (project) => {
    const k = key('spend', project, '');
    const editing = editKey === k;
    const v = shown('spend', project);
    return (
      <div className="kpi-srow" key={project}>
        <span className="kpi-sname"><span className="dot" style={{ background: PROJECT_COLOR[project] }} />{project}</span>
        <span className={`kpi-money${editing ? ' editing' : ''}`} onClick={() => canEdit && !editing && beginEdit('spend', project, '')}>
          <span className="pfx">RM</span>
          {editing ? (
            <input inputMode="decimal" autoComplete="off" value={draft} ref={editRef}
              onChange={(e) => setDraft(sanitize(e.target.value, false))}
              onKeyPress={(e) => { if (e.key.length === 1 && !/[0-9.,]/.test(e.key)) e.preventDefault(); }}
              onKeyDown={(e) => onKeyDown(e, 'spend', project, '')}
              onBlur={() => { if (navRef.current) { navRef.current = false; return; } commit('spend', project, '', draft); setEditKey(null); }} />
          ) : (
            <span className="kpi-money-val">{v != null ? Number(v).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="mk-page">
      <div className="mk-head">
        <div>
          <div className="mk-crumbs">Marketing &rsaquo; <b>KPI Insert</b></div>
          <h1>KPI Insert</h1>
          <div className="sub">{month} · leads by platform × project, spend per project</div>
        </div>
      </div>

      <div className="mk-banner ok">
        <span>✓</span>
        <span>Key in leads and spend — changes <b>save instantly</b> and feed the report. Every edit is recorded in the <b>edit log</b> below.</span>
      </div>

      {error && <div className="mk-error">{error}</div>}

      {/* LEADS */}
      <div className="mk-card kpi-card" style={{ '--kc': 'var(--rmc-green)' }}>
        <div className="mk-card-head"><h3><span className="kpi-ic" style={{ '--kc': 'var(--rmc-green)' }}>👤</span> Leads <span className="c-scope">platform × project</span></h3><span className="c-sub">number of leads · {month}</span></div>
        <div className="mk-tbl-scroll">
          <table className="kpi-matrix">
            <thead>
              <tr>
                <th className="pl">Platform</th>
                {PROJECTS.map((p) => <th key={p}><span className="dot" style={{ background: PROJECT_COLOR[p] }} />{PROJ_SHORT[p]}</th>)}
                <th className="rt">Total</th>
              </tr>
            </thead>
            <tbody>
              {PLATFORMS.map((pl) => (
                <tr key={pl}>
                  <td className="pl"><span className="dot" style={{ background: PLATFORM_DOT[pl] }} />{pl}</td>
                  {PROJECTS.map((pr) => leadCell(pr, pl))}
                  <td className="rt">{fmtInt(leadRowTotal(pl))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="pl">Total</td>
                {PROJECTS.map((pr) => <td key={pr}>{fmtInt(leadColTotal(pr))}</td>)}
                <td className="grand">{fmtInt(leadGrand)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* SPEND */}
      <div className="mk-card kpi-card" style={{ '--kc': '#3B5BDB' }}>
        <div className="mk-card-head"><h3><span className="kpi-ic" style={{ '--kc': '#3B5BDB' }}>💸</span> Spend <span className="c-scope">per project</span></h3><span className="c-sub">total marketing spend · {month}</span></div>
        <div className="kpi-slist">
          {PROJECTS.map((p) => spendRow(p))}
          <div className="kpi-srow total">
            <span className="kpi-sname">Total spend</span>
            <span className="kpi-money-val grand">{rm(spendTotal)}</span>
          </div>
        </div>
      </div>

      {/* EDIT LOG */}
      <div className="mk-card">
        <div className="mk-card-head"><h3>Edit log</h3><span className="c-sub">{log.length} change{log.length === 1 ? '' : 's'} · {month}</span></div>
        {log.length === 0 ? (
          <p className="mk-foot" style={{ margin: 0 }}>No edits yet this month.</p>
        ) : (
          <div className="mk-tbl-scroll">
            <table className="kpi-qtbl">
              <thead><tr><th>KPI</th><th>Dimension</th><th className="r">Change</th><th>By</th><th>When</th></tr></thead>
              <tbody>
                {log.map((r, i) => (
                  <tr key={i}>
                    <td><span className={`kpi-chip ${r.kpi}`}>{r.kpi === 'leads' ? 'Leads' : 'Spend'}</span></td>
                    <td>{r.kpi === 'leads' ? <>{r.platform} · <b>{r.project}</b></> : <b>{r.project}</b>}</td>
                    <td className="r kpi-delta">
                      {r.old_value != null && <span className="old">{r.kpi === 'leads' ? fmtInt(r.old_value) : rm(r.old_value)}</span>}
                      <span className="new">{r.kpi === 'leads' ? fmtInt(r.new_value) : rm(r.new_value)}</span>
                      {r.old_value == null && <span className="kpi-when"> (new)</span>}
                    </td>
                    <td>{r.changed_by}</td>
                    <td className="kpi-when">{fmtWhen(r.changed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default KpiInsert;
